import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import moment from 'moment';

import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '../../../../components/base/base.component';
import { PeopleService } from '../../../../services/people.service';
import { SharedService } from '../../../../services/shared.service';
import { EmployeeDeduction, EmployeeDeductionRate } from '../../../../models/employee-deductions';
import { CompanyEarningConfig } from '../../../../models/company-earning-config';
import { EmployeeEarning, EmployeeEarningRate } from '../../../../models/employee-earning';
import {
  formatDatePickerValue,
  parseDatePickerValue,
  parseDatePickerValueToString
} from '../../../../utils/datepicker-value-parser';
import { CompanyDeductionConfig } from '../../../../models/company-deduction-config';
import { getObjectDeltas } from '../../../../utils/object-deltas';
import { AuthService } from '../../../../services/auth.service';
import { AuditService } from '../../../../services/audit.service';
import { Person } from '../../../../models/person';
import { ToastService } from '../../../../services/toast.service';
import { EnumService } from '../../../../services/enum.service';
import { Select, Store } from '@ngxs/store';
import { ClientSelectionState } from '../../../../state/ClientSelectionState/ClientSelection.state';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { Enum } from '../../../../models/base';
import { PersonEditorState } from '../../../../state/PersonEditorState/PersonEditor.state';
import { catchError, map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { endOfDay, isAfter, isBefore, isEqual, startOfDay } from 'date-fns';
import {
  AddEmployeeDeduction,
  AddEmployeeEarning,
  ReloadEmployeeDeductions,
  ReloadEmployeeEarnings,
  UpdateEmployeeDeduction,
  UpdateEmployeeEarning
} from '../../../../state/PersonEditorState/PersonEditor.actions';

@Component({
  selector: 'app-earnings-and-deductions-tab',
  templateUrl: './earnings-and-deductions-tab.component.html',
  styleUrls: ['./earnings-and-deductions-tab.component.css'],
  standalone: false
})
export class EarningsAndDeductionsTabComponent extends BaseComponent implements OnInit, OnDestroy {
  // model
  earningsAndDeductionsForm: FormGroup;
  earningTypes: Enum[] = [];
  @Select(PersonEditorState.getPerson) person: Observable<Person>;
  @Select(PersonEditorState.getReadOnly) readOnly: Observable<boolean>;

  @Select(PersonEditorState.getDisplayedDeductions)
  getDisplayedDeductions: Observable<EmployeeDeduction[]>;
  // populate list in Add Deduction modal
  @Select(PersonEditorState.getAvailableDeductions)
  getAvailableDeductions: Observable<CompanyDeductionConfig[]>;

  // populate list in Add Earning modal
  @Select(PersonEditorState.getAvailableEarnings)
  getAvailableEarnings: Observable<CompanyEarningConfig[]>;
  @Select(PersonEditorState.getDisplayedEarnings)
  getDisplayedEarnings: Observable<EmployeeEarning[]>;

  // view logic
  autoPayThreshold: number = null;
  clientCode: string;
  collapseDeduction: boolean[] = [];
  collapseEarning: boolean[] = [];
  customDeductionError = '';
  customEarningError = '';
  deductionsModalForm: UntypedFormGroup;
  earningsModalForm: UntypedFormGroup;
  eMaxDate = {
    year: new Date().getFullYear() + 1,
    month: new Date().getMonth() + 1,
    day: new Date().getDate()
  };

  emptyEarningModal = true;
  // BUG 5199. user can 'save' an empty earning ONLY when editing an earning...
  isEditingEarning = false;
  selDeductionName: string = null;
  selDeductionRate: EmployeeDeductionRate = null;
  selEarningName: string = null;
  selEarningRate: EmployeeEarningRate = null;
  today = new Date();

  private readonly toastService = inject(ToastService);
  private destroy$ = new Subject<void>();
  constructor(
    private auditService: AuditService,
    public authService: AuthService,
    private formBuilder: UntypedFormBuilder,
    private ngModalService: NgbModal,
    private peopleService: PeopleService,
    private route: ActivatedRoute,
    private sharedService: SharedService,
    private enumService: EnumService,
    private store: Store
  ) {
    super();
  }

  get d() {
    return this.deductionsModalForm.controls;
  }

  get e() {
    return this.earningsModalForm.controls;
  }

  // #region PUBLIC METHODS

  // add rate link was clicked
  addDeductionRate(deduction: EmployeeDeduction, content) {
    this.store.selectOnce(PersonEditorState.getDeductionConfigs).subscribe(deductionConfigs => {
      // do not prefill rate fields
      const config = deductionConfigs.find(x => x.Id === deduction.Id);
      this.selDeductionName = config.Name;

      this.deductionsModalForm.patchValue(deduction);
      this.d.DeductionId.setValue(config.Id);
      this.onDeductionChange();
      this.d.Balance.setValue(deduction.LoanBalance);
      this.d.IsDisabled.setValue(!deduction.IsActive);

      this.ngModalService
        .open(content, {
          backdrop: 'static',
          keyboard: false,
          size: 'lg',
          windowClass: 'centered'
        })
        .result.then(
          result => {
            this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
              // success
              if (this.deductionsModalForm.valid) {
                const _deduction = structuredClone(deduction);
                const updatedDeduction = this._updateDeduction(deduction);
                const rate = this._createDeductionRate(updatedDeduction);
                if (person.Id) {
                  this.peopleService
                    .updateEmployeeDeduction(this.clientCode, person.Id, updatedDeduction)
                    .subscribe(
                      response => {
                        this.store
                          .dispatch(new UpdateEmployeeDeduction(response.Model))
                          .subscribe(() => {
                            this.toastService.show('Save successful');

                            this.sharedService.personEditsSaved.next();

                            const deltas = getObjectDeltas(
                              _deduction,
                              response.Model,
                              ['Rates', 'IntegrityHash', 'LoanBalance', 'Valid'],
                              config.Name
                            );
                            if (Object.keys(deltas).length > 0) {
                              this.auditService.auditEmployee(
                                `employee-deduction-updated`,
                                {
                                  ClientCode: this.clientCode,
                                  PersonId: person.Id,
                                  EmployeeNumber: person.EmployeeId,
                                  EmployeeName: `${person.LastName}, ${person.FirstName}`
                                },
                                deltas
                              );
                            }

                            const rDeltas = getObjectDeltas(
                              null,
                              rate,
                              ['LastChangedOn', 'LastEditorId', 'IntegrityHash'],
                              config.Name
                            );
                            this.auditService.auditEmployee(
                              `employee-deduction-rate-created`,
                              {
                                ClientCode: this.clientCode,
                                PersonId: person.Id,
                                EmployeeNumber: person.EmployeeId,
                                EmployeeName: `${person.LastName}, ${person.FirstName}`
                              },
                              rDeltas
                            );
                          });
                      },
                      error => {
                        // changes did not save successfully
                        this.store.dispatch(new ReloadEmployeeDeductions(this.clientCode));
                      }
                    );
                }
              }

              this._resetDeductionModal();
            });
          },
          () => {
            // dismiss
            this._resetDeductionModal();
          }
        );
    });
  }

  // add rate link was clicked
  addEarningRate(earning: EmployeeEarning, content) {
    this.store.selectOnce(PersonEditorState.getEarningConfigs).subscribe(earningConfigs => {
      // do not prefill rate specific fields
      const config = earningConfigs.find(x => x.Id === earning.Id);
      this.selEarningName = config.Name;

      this.e.EarningId.setValue(config.Id);
      this.e.HoursPaidPerPayroll.setValue(earning.HoursPaidPerPayroll);
      this.e.IsDisabled.setValue(!earning.IsActive);

      this.ngModalService
        .open(content, {
          backdrop: 'static',
          keyboard: false,
          size: 'lg',
          windowClass: 'centered'
        })
        .result.then(
          result => {
            this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
              // success
              if (this.earningsModalForm.valid) {
                const _earning = structuredClone(earning);
                const updatedEarning = this._updateEarning(earning);
                this._createEarningRate(updatedEarning).subscribe(rate => {
                  if (person.Id) {
                    this.peopleService
                      .updateEarning(this.clientCode, person.Id, updatedEarning)
                      .subscribe(
                        response => {
                          this.store
                            .dispatch(new UpdateEmployeeEarning(response.Model))
                            .subscribe(() => {
                              this.toastService.show('Save successful');

                              this.sharedService.personEditsSaved.next();

                              const deltas = getObjectDeltas(
                                _earning,
                                response.Model,
                                ['Rates', 'IntegrityHash', 'Valid'],
                                config.Name
                              );
                              if (Object.keys(deltas).length > 0) {
                                this.auditService.auditEmployee(
                                  `employee-earning-updated`,
                                  {
                                    ClientCode: this.clientCode,
                                    PersonId: person.Id,
                                    EmployeeNumber: person.EmployeeId,
                                    EmployeeName: `${person.LastName}, ${person.FirstName}`
                                  },
                                  deltas
                                );
                              }

                              if (rate) {
                                const rDeltas = getObjectDeltas(
                                  null,
                                  rate,
                                  ['LastChangedOn', 'LastEditorId', 'IntegrityHash'],
                                  config.Name
                                );
                                this.auditService.auditEmployee(
                                  `employee-earning-rate-created`,
                                  {
                                    ClientCode: this.clientCode,
                                    PersonId: person.Id,
                                    EmployeeNumber: person.EmployeeId,
                                    EmployeeName: `${person.LastName}, ${person.FirstName}`
                                  },
                                  rDeltas
                                );
                              }
                            });
                        },
                        error => {
                          // changes did not save successfully
                          this.store.dispatch(new ReloadEmployeeEarnings(this.clientCode));
                        }
                      );
                  }
                });
              }

              this._resetEarningModal();
            });
          },
          () => {
            // dismiss
            this._resetEarningModal();
          }
        );
    });
  }

  canDeductionHaveRates(deductionId: string) {
    return this.store.select(PersonEditorState.canDeductionHaveRates(deductionId));
  }

  canEarningHaveRates(earningId) {
    return this.store.select(PersonEditorState.canEarningHaveRates(earningId));
  }

  canEarningSetHours(earningId) {
    return this.store.select(PersonEditorState.canEarningSetHours(earningId));
  }

  canHaveAnnualLimit(id): Observable<boolean> {
    return this.store.select(PersonEditorState.canDeductionHaveAnnualLimit(id));
  }

  canEditDisbursements(id): Observable<boolean> {
    return this.store.select(PersonEditorState.canDeductionEditDisbursements(id));
  }

  canOfferMedIns(id): Observable<boolean> {
    return this.store.select(PersonEditorState.canDeductionOfferMedIns(id));
  }

  canShowDisbursements(id): Observable<boolean> {
    return this.store.select(PersonEditorState.canDeductionShowDisbursements(id));
  }

  canHaveDisposableLimits(id): Observable<boolean> {
    return this.store.select(PersonEditorState.canDeductionHaveDisposableLimits(id));
  }

  checkDeductionEffectiveDate(selDeductionId: string = '') {
    // bug 5272
    // do not allow an effective date already used by one of this deduction's rates
    this.customDeductionError = '';
    const date = this.d.Date.value;

    if (date) {
      this.store
        .selectOnce(PersonEditorState.getEmployeeDeductions)
        .subscribe(employeeDeductions => {
          // do not allow user to pick an effective date that is already
          // in use by another rate on this deduction
          const fDate = parseDatePickerValue(date);
          const deduction = employeeDeductions.find(x => x.Id == selDeductionId);

          deduction.Rates.forEach(x => {
            // ignore the currently selected rate if editing
            if (
              (!this.selDeductionRate || x.EffectiveDate != this.selDeductionRate.EffectiveDate) &&
              isEqual(endOfDay(new Date(x.EffectiveDate)), endOfDay(fDate))
            ) {
              this.customDeductionError = '*This effective date is already in use by another rate';
            }
          });

          // only allow 1 future effective rate per deduction
          if (!this.customDeductionError && isAfter(endOfDay(fDate), endOfDay(new Date()))) {
            deduction.Rates.forEach(x => {
              if (
                (!this.selDeductionRate ||
                  x.EffectiveDate != this.selDeductionRate.EffectiveDate) &&
                moment(new Date(x.EffectiveDate)).isAfter(new Date(), 'day')
              ) {
                this.customDeductionError =
                  '*There can only be 1 future effective rate per deduction';
              }
            });
          }
        });
    }
  }

  checkEarningEffectiveDate(selEarningId: string = '') {
    // bug 5272
    // do not allow an effective date already used by one of this earning's rates
    this.customEarningError = '';
    const date = this.e.Date.value;

    if (date) {
      this.store.selectOnce(PersonEditorState.getEmployeeEarnings).subscribe(employeeEarnings => {
        // do not allow user to pick an effective date that is already
        // in use by another rate on this earning
        const fDate = parseDatePickerValue(date);
        const earning = employeeEarnings.find(x => x.Id == selEarningId);

        earning.Rates.forEach(x => {
          // ignore the currently selected rate if editing
          if (
            (!this.selEarningRate || x.EffectiveDate != this.selEarningRate.EffectiveDate) &&
            moment(fDate).isSame(new Date(x.EffectiveDate), 'day')
          ) {
            this.customEarningError = '*This effective date is already in use by another rate';
          }
        });

        // only allow 1 future effective rate per earning
        if (!this.customEarningError && moment(fDate).isAfter(new Date(), 'day')) {
          earning.Rates.forEach(x => {
            if (
              (!this.selEarningRate || x.EffectiveDate != this.selEarningRate.EffectiveDate) &&
              moment(new Date(x.EffectiveDate)).isAfter(new Date(), 'day')
            ) {
              this.customEarningError = '*There can only be 1 future effective rate per earning';
            }
          });
        }
      });
    }
  }

  collapseDeductions(index: number) {
    this.collapseDeduction[index] = !this.collapseDeduction[index];
  }

  collapseEarnings(index: number) {
    this.collapseEarning[index] = !this.collapseEarning[index];
  }

  editDeduction(deduction: EmployeeDeduction, rate: EmployeeDeductionRate, content) {
    this.store.selectOnce(PersonEditorState.getDeductionConfigs).subscribe(deductionConfigs => {
      const config = deductionConfigs.find(x => x.Id === deduction.Id);
      this.selDeductionName = config.Name;
      this.selDeductionRate = rate;

      this.deductionsModalForm.patchValue(deduction);
      this.d.DeductionId.setValue(config.Id);
      this.onDeductionChange();
      this.d.Balance.setValue(deduction.LoanBalance);

      if (rate) {
        this.d.Amount.setValue(rate.Amount);
        this.d.Date.setValue(formatDatePickerValue(rate.EffectiveDate));
        this.d.Date.setValidators(Validators.required);
      } else {
        this.d.Date.clearValidators();
      }

      this.d.IsDisabled.setValue(!deduction.IsActive);

      this.ngModalService
        .open(content, {
          backdrop: 'static',
          keyboard: false,
          size: 'lg',
          windowClass: 'centered'
        })
        .result.then(
          result => {
            this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
              this.store
                .selectOnce(PersonEditorState.getEmployeeDeductions)
                .subscribe(employeeDeductions => {
                  // success
                  if (this.deductionsModalForm.valid) {
                    const _deduction = { ...deduction };
                    const _rate = { ...rate };

                    const index = employeeDeductions.indexOf(deduction);
                    const updatedDeduction = this._updateDeduction(deduction);
                    if (config.CanHaveRates) {
                      const updatedRate = this._updateDeductionRate(rate);
                      const rIndex = updatedDeduction.Rates.findIndex(
                        x => x.SequenceNumber == updatedRate.SequenceNumber
                      );
                      updatedDeduction.Rates[rIndex] = updatedRate;
                    } else {
                      updatedDeduction.Rates = [];
                    }

                    if (person.Id) {
                      const rIndex = updatedDeduction.Rates.findIndex(
                        x => x.SequenceNumber == rate.SequenceNumber
                      );
                      this.peopleService
                        .updateEmployeeDeduction(this.clientCode, person.Id, updatedDeduction)
                        .subscribe(
                          response => {
                            this.store
                              .dispatch(new UpdateEmployeeDeduction(response.Model))
                              .subscribe(() => {
                                this.store
                                  .selectOnce(PersonEditorState.getEmployeeDeductions)
                                  .subscribe(updatedDeductions => {
                                    this.toastService.show('Save successful');

                                    this.sharedService.personEditsSaved.next();

                                    const deltas = getObjectDeltas(
                                      _deduction,
                                      response.Model,
                                      ['Rates', 'IntegrityHash', 'LoanBalance', 'Valid'],
                                      config.Name
                                    );
                                    if (Object.keys(deltas).length > 0) {
                                      this.auditService.auditEmployee(
                                        `employee-deduction-updated`,
                                        {
                                          ClientCode: this.clientCode,
                                          PersonId: person.Id,
                                          EmployeeNumber: person.EmployeeId,
                                          EmployeeName: `${person.LastName}, ${person.FirstName}`
                                        },
                                        deltas
                                      );
                                    }

                                    if (config.CanHaveRates) {
                                      const rDeltas = getObjectDeltas(
                                        _rate,
                                        updatedDeductions[index].Rates[rIndex],
                                        ['LastChangedOn', 'LastEditorId', 'IntegrityHash'],
                                        config.Name
                                      );
                                      if (Object.keys(rDeltas).length > 0) {
                                        this.auditService.auditEmployee(
                                          `employee-deduction-rate-updated`,
                                          {
                                            ClientCode: this.clientCode,
                                            PersonId: person.Id,
                                            EmployeeNumber: person.EmployeeId,
                                            EmployeeName: `${person.LastName}, ${person.FirstName}`
                                          },
                                          rDeltas
                                        );
                                      }
                                    }
                                  });
                              });
                          },
                          error => {
                            // changes did not save successfully
                            this.store.dispatch(new ReloadEmployeeDeductions(this.clientCode));
                          }
                        );
                    }
                  }

                  this._resetDeductionModal();
                });
            });
          },
          () => {
            // dismiss
            this._resetDeductionModal();
          }
        );
    });
  }

  // edit earning link was clicked
  editEarning(earning: EmployeeEarning, rate: EmployeeEarningRate, content) {
    this.store.selectOnce(PersonEditorState.getEarningConfigs).subscribe(earningConfigs => {
      this.isEditingEarning = true;
      const config = earningConfigs.find(x => x.Id === earning.Id);
      this.selEarningName = config.Name;
      this.selEarningRate = rate;

      this.e.EarningId.setValue(config.Id);
      this.e.HoursPaidPerPayroll.setValue(earning.HoursPaidPerPayroll);
      this.e.IsDisabled.setValue(!earning.IsActive);

      if (config.CanHaveRates && rate) {
        this.e.Amount.setValue(rate.Amount);
        this.e.Date.setValue(formatDatePickerValue(rate.EffectiveDate));
      } else {
        this.e.Date.setValue(formatDatePickerValue(new Date()));
      }

      this.setEarningAmountValidation();
      this.isEarningModalEmpty();

      this.ngModalService
        .open(content, {
          backdrop: 'static',
          keyboard: false,
          size: 'lg',
          windowClass: 'centered'
        })
        .result.then(
          result => {
            this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
              // success
              if (this.earningsModalForm.valid) {
                const _earning = { ...earning };

                if (!rate) {
                  rate = new EmployeeEarningRate();
                }

                const _rate = { ...rate };
                const updatedEarning = this._updateEarning(earning);
                this._updateEarningRate(updatedEarning, rate).subscribe(updatedRate => {
                  if (person.Id) {
                    const rIndex = updatedEarning.Rates.findIndex(
                      x => x.SequenceNumber == updatedRate.SequenceNumber
                    );
                    updatedEarning.Rates[rIndex] = updatedRate;
                    this.store
                      .selectOnce(PersonEditorState.canEarningHaveRates(updatedEarning.Id))
                      .subscribe(canHaveRates => {
                        if (!canHaveRates) {
                          updatedEarning.Rates = [];
                        }
                        this.peopleService
                          .updateEarning(this.clientCode, person.Id, updatedEarning)
                          .subscribe(
                            response => {
                              this.store
                                .dispatch(new UpdateEmployeeEarning(response.Model))
                                .subscribe(() => {
                                  this.store
                                    .selectOnce(PersonEditorState.getEmployeeEarnings)
                                    .subscribe(updatedEarnings => {
                                      this.toastService.show('Save successful');

                                      this.sharedService.personEditsSaved.next();

                                      const deltas = getObjectDeltas(
                                        _earning,
                                        response.Model,
                                        ['Rates', 'IntegrityHash', 'Valid'],
                                        config.Name
                                      );
                                      if (Object.keys(deltas).length > 0) {
                                        this.auditService.auditEmployee(
                                          `employee-earning-updated`,
                                          {
                                            ClientCode: this.clientCode,
                                            PersonId: person.Id,
                                            EmployeeNumber: person.EmployeeId,
                                            EmployeeName: `${person.LastName}, ${person.FirstName}`
                                          },
                                          deltas
                                        );
                                      }

                                      const index = updatedEarnings.findIndex(
                                        x => x.Id == updatedEarning.Id
                                      );
                                      const rDeltas = getObjectDeltas(
                                        _rate,
                                        updatedEarnings[index].Rates[rIndex],
                                        ['LastChangedOn', 'LastEditorId', 'IntegrityHash'],
                                        config.Name
                                      );
                                      if (Object.keys(rDeltas).length > 0) {
                                        this.auditService.auditEmployee(
                                          `employee-earning-rate-updated`,
                                          {
                                            ClientCode: this.clientCode,
                                            PersonId: person.Id,
                                            EmployeeNumber: person.EmployeeId,
                                            EmployeeName: `${person.LastName}, ${person.FirstName}`
                                          },
                                          rDeltas
                                        );
                                      }
                                    });
                                });
                            },
                            error => {
                              // changes did not save successfully
                              this.store.dispatch(new ReloadEmployeeEarnings(this.clientCode));
                            }
                          );
                      });
                  }
                  this._resetEarningModal();
                });
              } else {
                this._resetEarningModal();
              }
            });
          },
          () => {
            // dismiss
            this._resetEarningModal();
          }
        );
    });
  }

  getBenefitType(id): Observable<string> {
    return this.store.select(PersonEditorState.getBenefitType(id));
  }

  getCurrentDeductionRate(deduction: EmployeeDeduction, override: boolean = false) {
    // get most recent effective date <= today (no future rates)
    let rate = null;

    deduction.Rates.forEach(r => {
      if (moment(r.EffectiveDate).isSameOrBefore(new Date(), 'day')) {
        if (rate === null || moment(r.EffectiveDate).isSameOrAfter(rate.EffectiveDate, 'day')) {
          rate = r;
        }
      }
    });

    // if there is only 1 rate that is future dated and override === true
    // return future date
    if (rate === null && override) {
      rate = this.getFutureDeductionRate(deduction);
    }

    return rate;
  }

  getCurrentEarningRate(earning: EmployeeEarning, override: boolean = false) {
    // get most recent effective date <= today (no future rates)
    let rate = null;

    earning.Rates.forEach(r => {
      if (moment(r.EffectiveDate).isSameOrBefore(new Date(), 'day')) {
        if (rate === null || moment(r.EffectiveDate).isSameOrAfter(rate.EffectiveDate, 'day')) {
          rate = r;
        }
      }
    });

    // if there is only 1 rate that is future dated and override === true
    // return future date
    if (rate === null && override) {
      rate = this.getFutureEarningRate(earning);
    }

    return rate;
  }

  // '$' or empty string
  getDeductionAmountPrefix(deductionId: string) {
    return this.store.select(PersonEditorState.getDeductionAmountPrefix(deductionId));
  }

  // '%'  or empty string
  getDeductionAmountSuffix(deductionId: string) {
    return this.store.select(PersonEditorState.getDeductionAmountSuffix(deductionId));
  }

  getDeductionConfig(deductionId: string): Observable<CompanyDeductionConfig> {
    return this.store.select(PersonEditorState.getDeductionConfig(deductionId));
  }

  getDeductionName(id: string): Observable<string> {
    return this.store.select(PersonEditorState.getDeductionName(id));
  }

  // '$' or empty string
  getEarningAmountPrefix(earningId: string) {
    return this.store.select(PersonEditorState.getEarningAmountPrefix(earningId));
  }

  // '%' or empty string
  getEarningAmountSuffix(earningId: string) {
    return this.store.select(PersonEditorState.getEarningAmountSuffix(earningId));
  }

  getEarningCalc(id) {
    return this.store.select(PersonEditorState.getEarningCalc(id));
  }

  getEarningName(id) {
    return this.store.select(PersonEditorState.getEarningName(id));
  }

  getFutureDeductionRate(deduction: EmployeeDeduction) {
    let rate = null;

    deduction.Rates.forEach(r => {
      if (moment(r.EffectiveDate).isAfter(new Date(), 'day')) {
        rate = r;
      }
    });

    return rate;
  }

  getFutureEarningRate(earnings: EmployeeEarning) {
    let rate = null;

    earnings.Rates.forEach(r => {
      if (moment(r.EffectiveDate).isAfter(new Date(), 'day')) {
        rate = r;
      }
    });

    return rate;
  }

  getPastDeductionRates(deduction: EmployeeDeduction) {
    const rates = [];

    // fetch all earnings of today or before
    deduction.Rates.forEach(r => {
      if (moment(r.EffectiveDate).isSameOrBefore(new Date(), 'day')) {
        rates.push(r);
      }
    });

    if (rates.length > 0) {
      // order by date desc
      rates.sort((a: EmployeeEarningRate, b: EmployeeEarningRate) => {
        return new Date(b.EffectiveDate).getTime() - new Date(a.EffectiveDate).getTime();
      });

      // drop most recent date since this is the current earning rate
      rates.shift();
    }

    return rates;
  }

  getPastEarningRates(earning: EmployeeEarning) {
    const rates = [];

    // fetch all earnings of today or before
    earning.Rates.forEach(r => {
      if (moment(r.EffectiveDate).isSameOrBefore(new Date(), 'day')) {
        rates.push(r);
      }
    });

    if (rates.length > 0) {
      // order by date desc
      rates.sort((a: EmployeeEarningRate, b: EmployeeEarningRate) => {
        return new Date(b.EffectiveDate).getTime() - new Date(a.EffectiveDate).getTime();
      });

      // drop most recent date since this is the current earning rate
      rates.shift();
    }

    return rates;
  }

  getThirdPartyPayees(id) {
    return this.store.select(PersonEditorState.getThirdPartyPayees(id));
  }

  isDateBeforeToday(date): boolean {
    const d = parseDatePickerValue(date);
    if (d) {
      return isBefore(startOfDay(d), startOfDay(new Date()));
    } else {
      return false;
    }
  }

  isDeductionRateExpanded(index: number): boolean {
    return this.collapseDeduction[index];
  }

  isEarningModalEmpty() {
    if (this.e.IsDisabled.value || this.e.Amount.value || this.e.HoursPaidPerPayroll.value) {
      this.emptyEarningModal = false;
    } else {
      this.emptyEarningModal = true;
    }
  }

  isEarningRateExpanded(index: number): boolean {
    return this.collapseEarning[index];
  }

  ngOnInit() {
    this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
      this.clientCode = code;
      const obs = [this.enumService.get('EarningType')];

      forkJoin(obs).subscribe(response => {
        this.earningTypes = response[0];
        this.init();
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private init() {
    this.earningsAndDeductionsForm = this.formBuilder.group({
      Earnings: this.formBuilder.array([]),
      Deductions: this.formBuilder.array([])
    });
    this.store
      .select(PersonEditorState.markFormTouched)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const controls = this.earningsAndDeductionsForm.controls;
        Object.keys(controls).forEach(key => {
          controls[key].markAsTouched();
        });
      });
    this.earningsModalForm = this.formBuilder.group({
      EarningId: new UntypedFormControl('', Validators.required),
      Amount: new UntypedFormControl('', [Validators.required, Validators.max(9999999.9999)]),
      HoursPaidPerPayroll: new UntypedFormControl('0', Validators.max(9999.99)),
      Date: new UntypedFormControl('', [Validators.required]),
      IsDisabled: new UntypedFormControl()
    });

    this.deductionsModalForm = this.formBuilder.group({
      DeductionId: new UntypedFormControl('', Validators.required),
      Amount: new UntypedFormControl('', [Validators.max(9999999.99), Validators.required]),
      AnnualLimit: new UntypedFormControl(),
      LoanNumber: new UntypedFormControl(),
      Balance: new UntypedFormControl(),
      Date: new UntypedFormControl(),
      IsDisabled: new UntypedFormControl(),
      DisposableLimitType: new UntypedFormControl(),
      DisposableLimit: new UntypedFormControl(0),
      LoanId: new UntypedFormControl(),
      SelectedThirdPartyPayee: new UntypedFormControl(),
      CaseNumber: new UntypedFormControl(),
      EmployerOffersFamilyMedIns: new UntypedFormControl()
    });

    this.deductionsModalForm.get('DisposableLimitType').valueChanges.subscribe(val => {
      const limit = this.deductionsModalForm.get('DisposableLimit');
      limit.clearValidators();

      if (val === '1') {
        limit.setValidators([Validators.required, Validators.max(100)]);
      } else if (val === '2') {
        limit.setValidators([Validators.required]);
      } else {
        limit.setValue('');
      }

      limit.updateValueAndValidity();
    });

    // add item to deduction/earnings collapse arrays for each available type
    this.store.selectOnce(PersonEditorState.getDeductionConfigs).subscribe(deductionConfigs => {
      deductionConfigs.forEach(type => {
        this.collapseDeduction.push(false);
      });
    });

    this.store.selectOnce(PersonEditorState.getEarningConfigs).subscribe(earningConfigs => {
      earningConfigs.forEach(type => {
        this.collapseEarning.push(false);
      });
    });

    this._setEarningAutoPayThreshold();

    this.store.selectOnce(PersonEditorState.getReadOnly).subscribe(readOnly => {
      if (readOnly) {
        this.disableAllFormControls(this.deductionsModalForm);
        this.disableAllFormControls(this.earningsModalForm);
      }
    });
  }

  // add deduction button was clicked
  openDeductions(content) {
    this.deductionsModalForm.get('Date').setValue(formatDatePickerValue(new Date()));
    this.deductionsModalForm.get('DisposableLimit').setValue('0');
    this.ngModalService
      .open(content, {
        backdrop: 'static',
        keyboard: false,
        size: 'lg',
        windowClass: 'centered'
      })
      .result.then(
        result => {
          this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
            this.store
              .selectOnce(PersonEditorState.getDeductionConfigs)
              .subscribe(deductionConfigs => {
                this.store
                  .selectOnce(PersonEditorState.getEmployeeDeductions)
                  .subscribe(employeeDeductions => {
                    // success
                    const modalForm = this.deductionsModalForm.controls;
                    if (this.deductionsModalForm.valid) {
                      // push saved data from modal to person deductions
                      let deduction = structuredClone(
                        employeeDeductions.find(x => x.Id == this.d.DeductionId.value)
                      );

                      if (!deduction) {
                        deduction = new EmployeeDeduction();
                        deduction.Id = this.d.DeductionId.value;

                        this.processSavedDeduction(
                          deduction,
                          deductionConfigs,
                          person,
                          modalForm
                        ).subscribe(updatedDeduction => {
                          this.store.dispatch(new AddEmployeeDeduction(updatedDeduction));
                        });
                      } else {
                        this.processSavedDeduction(
                          deduction,
                          deductionConfigs,
                          person,
                          modalForm
                        ).subscribe();
                      }
                    }
                    this._resetDeductionModal();
                  });
              });
          });
        },
        () => {
          // dismiss
          this._resetDeductionModal();
        }
      );
  }

  private processSavedDeduction(
    deduction: EmployeeDeduction,
    deductionConfigs: CompanyDeductionConfig[],
    person: Person,
    modalForm
  ) {
    deduction.Id = modalForm.DeductionId.value;
    deduction.Rates = [];
    const updatedDeduction = this._updateDeduction(deduction);
    let config = deductionConfigs.find(x => x.Id == updatedDeduction.Id);
    let rate;
    if (config.CanHaveRates) {
      rate = this._createDeductionRate(updatedDeduction);
    }
    if (person.Id) {
      return this.peopleService
        .updateEmployeeDeduction(this.clientCode, person.Id, updatedDeduction)
        .pipe(
          mergeMap(response => {
            return this.store.dispatch(new UpdateEmployeeDeduction(response.Model)).pipe(
              tap(() => {
                this.toastService.show('Save successful');

                this.sharedService.personEditsSaved.next();

                const config = deductionConfigs.find(x => x.Id == updatedDeduction.Id);
                const deltas = getObjectDeltas(
                  null,
                  response.Model,
                  ['Rates', 'IntegrityHash', 'LoanBalance', 'Valid'],
                  config.Name
                );
                this.auditService.auditEmployee(
                  `employee-deduction-updated`,
                  {
                    ClientCode: this.clientCode,
                    PersonId: person.Id,
                    EmployeeNumber: person.EmployeeId,
                    EmployeeName: `${person.LastName}, ${person.FirstName}`
                  },
                  deltas
                );

                if (config.CanHaveRates) {
                  const rDeltas = getObjectDeltas(
                    null,
                    rate,
                    ['LastChangedOn', 'LastEditorId', 'IntegrityHash'],
                    config.Name
                  );
                  if (Object.keys(rDeltas).length > 0) {
                    this.auditService.auditEmployee(
                      `employee-deduction-rate-created`,
                      {
                        ClientCode: this.clientCode,
                        PersonId: person.Id,
                        EmployeeNumber: person.EmployeeId,
                        EmployeeName: `${person.LastName}, ${person.FirstName}`
                      },
                      rDeltas
                    );
                  }
                }
              }),
              map(() => null)
            );
          }),
          catchError(error => {
            // changes did not save successfully
            this.store.dispatch(new ReloadEmployeeDeductions(this.clientCode));
            return of(null);
          })
        );
    }
    return of(updatedDeduction);
  }

  // add earnings button was clicked
  openEarnings(content) {
    this.earningsModalForm.get('Date').setValue(formatDatePickerValue(new Date()));
    this.ngModalService
      .open(content, {
        backdrop: 'static',
        keyboard: false,
        size: 'lg',
        windowClass: 'centered'
      })
      .result.then(
        result => {
          this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
            this.store.selectOnce(PersonEditorState.getEarningConfigs).subscribe(earningConfigs => {
              this.store
                .selectOnce(PersonEditorState.getEmployeeEarnings)
                .subscribe(employeeEarnings => {
                  // success
                  if (this.earningsModalForm.valid) {
                    // push saved data from earningsModalForm to person earnings
                    let earning = employeeEarnings.find(x => x.Id == this.e.EarningId.value);

                    if (!earning) {
                      earning = new EmployeeEarning();
                      earning.Id = this.e.EarningId.value;
                      this.processSavedEarning(earning, person, earningConfigs).subscribe(
                        updated => {
                          this.store.dispatch(new AddEmployeeEarning(updated));
                        }
                      );
                    } else {
                      this.processSavedEarning(
                        structuredClone(earning),
                        person,
                        earningConfigs
                      ).subscribe();
                    }
                  }
                });
            });
            this._resetEarningModal();
          });
        },
        () => {
          // dismiss
          this._resetEarningModal();
        }
      );
  }

  private processSavedEarning(earning: EmployeeEarning, person: Person, earningConfigs) {
    earning.Rates = [];
    const updatedEarning = this._updateEarning(earning);
    return this._createEarningRate(updatedEarning).pipe(
      mergeMap(rate => {
        if (person.Id) {
          const config = earningConfigs.find(x => x.Id == updatedEarning.Id);
          return this.peopleService.updateEarning(this.clientCode, person.Id, updatedEarning).pipe(
            mergeMap(response => {
              return this.store.dispatch(new UpdateEmployeeEarning(response.Model)).pipe(
                tap(() => {
                  this.toastService.show('Save successful');

                  this.sharedService.personEditsSaved.next();

                  const deltas = getObjectDeltas(
                    null,
                    response.Model,
                    ['Rates', 'IntegrityHash', 'Valid'],
                    config.Name
                  );
                  this.auditService.auditEmployee(
                    `employee-earning-updated`,
                    {
                      ClientCode: this.clientCode,
                      PersonId: person.Id,
                      EmployeeNumber: person.EmployeeId,
                      EmployeeName: `${person.LastName}, ${person.FirstName}`
                    },
                    deltas
                  );

                  const rDeltas = getObjectDeltas(
                    null,
                    rate,
                    ['LastChangedOn', 'LastEditorId', 'IntegrityHash'],
                    config.Name
                  );
                  if (Object.keys(rDeltas).length > 0) {
                    this.auditService.auditEmployee(
                      `employee-earning-rate-created`,
                      {
                        ClientCode: this.clientCode,
                        PersonId: person.Id,
                        EmployeeNumber: person.EmployeeId,
                        EmployeeName: `${person.LastName}, ${person.FirstName}`
                      },
                      rDeltas
                    );
                  }
                }),
                map(() => updatedEarning)
              );
            }),
            catchError(error => {
              this.store.dispatch(new ReloadEmployeeEarnings(this.clientCode));
              return of(updatedEarning);
            })
          );
        } else {
          return of(updatedEarning);
        }
      })
    );
  }
  onEarningDisable() {
    this.setEarningAmountValidation();
    this.isEarningModalEmpty();
  }

  setEarningAmountValidation() {
    // setup all validation logic for earning modal
    const id = this.e.EarningId.value;
    const disabled = this.e.IsDisabled.value;
    if (id) {
      this.store.selectOnce(PersonEditorState.getEarningConfigs).subscribe(earningConfigs => {
        const config = earningConfigs.find(x => x.Id == id);

        if (!config.CanHaveRates) {
          this.e.Amount.clearValidators();
        } else if (config.IsPercentageEarning && !disabled) {
          this.e.Amount.setValidators([Validators.max(100.0), Validators.required]);
        } else if (config.IsPercentageEarning && disabled) {
          this.e.Amount.setValidators([Validators.max(100.0)]);
        } else if (!config.IsPercentageEarning && !disabled) {
          this.e.Amount.setValidators([Validators.max(9999999.99), Validators.required]);
        } else if (!config.IsPercentageEarning && disabled) {
          this.e.Amount.setValidators([Validators.max(9999999.99)]);
        }

        this.e.Amount.updateValueAndValidity();
      });
    }
  }

  onEarningTypeChange() {
    // setup all validation logic for earning modal
    this.e.Amount.setValue(0);
    this.e.HoursPaidPerPayroll.setValue(0);
    this.setEarningAmountValidation();
    this.isEarningModalEmpty();
  }

  onDeductionChange() {
    this.store.selectOnce(PersonEditorState.getDeductionConfigs).subscribe(deductionConfigs => {
      this.store
        .selectOnce(PersonEditorState.getEmployeeDeductions)
        .subscribe(employeeDeductions => {
          // setup all validation logic for deduction modal
          const id = this.d.DeductionId.value;
          const disabled = this.d.IsDisabled.value;
          const deduction = employeeDeductions.find(x => x.Id == id);

          if (id) {
            const config = deductionConfigs.find(x => x.Id === id);
            if (config.RequiresDisposableLimit && !disabled) {
              this.d.DisposableLimit.setValidators([Validators.required]);
              this.d.DisposableLimitType.setValidators([Validators.required]);
            } else {
              this.d.DisposableLimit.clearValidators();
              this.d.DisposableLimitType.clearValidators();
            }

            // Bug 5253 - if the deduction requires disbursements to be specified at the employee
            // level, make SelectedThirdPartyPayee a required field
            if (config.ShowDisbursementControls && config.DisbursementAtEmployee && !disabled) {
              this.d.SelectedThirdPartyPayee.setValidators([Validators.required]);
            } else {
              this.d.SelectedThirdPartyPayee.clearValidators();
            }

            // prefill the third party payee selection from the config if one is passed
            // sometimes client deduction config.ThirdPartyPayee = -1...
            if (
              !this.d.SelectedThirdPartyPayee.value &&
              config.ThirdPartyPayee &&
              config.ThirdPartyPayee > 0
            ) {
              this.d.SelectedThirdPartyPayee.setValue(config.ThirdPartyPayee);
            }

            this.d.SelectedThirdPartyPayee.updateValueAndValidity();

            this.d.DisposableLimit.updateValueAndValidity();
            this.d.DisposableLimitType.updateValueAndValidity();

            if (config.CanHaveAnnualLimit) {
              if (config.IsLoan) {
                this.d.AnnualLimit.setValidators([Validators.max(9999999.99), Validators.required]);
              } else {
                this.d.AnnualLimit.setValidators([Validators.max(9999999.99)]);
              }
            } else {
              this.d.AnnualLimit.clearValidators();
            }

            if (!config.CanHaveRates) {
              this.d.Amount.clearValidators();
              this.d.Date.clearValidators();
            } else if (config.IsPercentageDeduction && !disabled) {
              this.d.Amount.setValidators([Validators.max(100.0), Validators.required]);
            } else if (config.IsPercentageDeduction && disabled) {
              this.d.Amount.setValidators([Validators.max(100.0)]);
            } else if (!config.IsPercentageDeduction && !disabled) {
              this.d.Amount.setValidators([Validators.max(9999999.99), Validators.required]);
            } else if (!config.IsPercentageDeduction && disabled) {
              this.d.Amount.setValidators([Validators.max(9999999.99)]);
            }

            this.d.AnnualLimit.updateValueAndValidity();
            this.d.Amount.updateValueAndValidity();

            if (deduction) {
              this.d.Balance.setValue(deduction.LoanBalance);
            }

            if (config.CanHaveRates) {
              this.d.Date.setValidators([Validators.required]);
            }
          }
        });
    });
  }

  // #endregion

  // #region PRIVATE METHODS

  private _createDeductionRate(deduction: EmployeeDeduction): EmployeeDeductionRate {
    const rate = new EmployeeDeductionRate();
    rate.Amount = this.d.Amount.value || 0;
    rate.EffectiveDate = parseDatePickerValueToString(this.d.Date.value);
    rate.LastChangedOn = new Date();
    rate.LastEditorId = this.authService.getCurrentUserName();

    deduction.Rates.push(rate);
    return rate;
  }

  private _createEarningRate(earning: EmployeeEarning): Observable<EmployeeEarningRate> {
    return this.store.selectOnce(PersonEditorState.getEarningConfigs).pipe(
      map(earningConfigs => {
        const config = earningConfigs.find(x => x.Id == earning.Id);

        if (config.CanHaveRates) {
          const rate = new EmployeeEarningRate();
          rate.Amount = this.e.Amount.value || 0;
          rate.EffectiveDate = parseDatePickerValueToString(this.e.Date.value);
          rate.LastChangedOn = new Date();
          rate.LastEditorId = this.authService.getCurrentUserName();

          earning.Rates.push(rate);
          return rate;
        } else {
          return null;
        }
      })
    );
  }

  private _resetDeductionModal() {
    this.customDeductionError = '';
    this.selDeductionName = null;
    this.selDeductionRate = null;
    this.deductionsModalForm.reset();
  }

  private _resetEarningModal() {
    this.isEditingEarning = false;
    this.emptyEarningModal = true;
    this.customEarningError = '';
    this.selEarningName = null;
    this.selEarningRate = null;
    this.earningsModalForm.reset();
  }

  private _setEarningAutoPayThreshold() {
    this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
      // FIXME: this is hardcoded based on spec from Jane on 2019-08-09
      // earning auto pay hours have max limit based on person's pay frequency
      // WARNING: given ids do not match API responses for Pay Frequencies
      const limits = {
        1: 100, // Weekly
        2: 400, // Biweekly
        3: 400, // Semi-Monthly
        4: 450 // Monthly
      };

      this.store.selectOnce(PersonEditorState.getPayFrequency).subscribe(payFrequency => {
        let frequency = 0;
        if (person && person.PayFrequency) {
          frequency = person.PayFrequency;
        } else if (payFrequency) {
          frequency = payFrequency;
        }

        if (limits[frequency]) {
          this.autoPayThreshold = limits[frequency];
          this.e.HoursPaidPerPayroll.setValidators([Validators.max(this.autoPayThreshold)]);
        } else {
          this.e.HoursPaidPerPayroll.clearValidators();
        }

        this.e.HoursPaidPerPayroll.updateValueAndValidity();
      });
    });
  }

  private _updateDeduction(deductionInput: EmployeeDeduction) {
    const deduction = structuredClone(deductionInput);
    deduction.IsActive = !this.d.IsDisabled.value;
    deduction.DisposableLimit = this.d.DisposableLimit.value || 0;
    deduction.DisposableLimitType = this.d.DisposableLimitType.value;
    deduction.AnnualLimit = this.d.AnnualLimit.value || 0;
    deduction.LoanId = this.d.LoanId.value;
    deduction.SelectedThirdPartyPayee = this.d.SelectedThirdPartyPayee.value || 0;
    deduction.CaseNumber = this.d.CaseNumber.value;
    deduction.EmployerOffersFamilyMedIns = this.d.EmployerOffersFamilyMedIns.value || false;
    return deduction;
  }

  private _updateDeductionRate(rateInput: EmployeeEarningRate) {
    const rate = structuredClone(rateInput);
    rate.Amount = this.d.Amount.value || 0;
    rate.EffectiveDate = parseDatePickerValueToString(this.d.Date.value);
    rate.LastChangedOn = new Date();
    rate.LastEditorId = this.authService.getCurrentUserName();
    return rate;
  }

  private _updateEarning(earningInput: EmployeeEarning) {
    const earning = structuredClone(earningInput);
    earning.HoursPaidPerPayroll = this.e.HoursPaidPerPayroll.value || 0;
    earning.IsActive = !this.e.IsDisabled.value;
    return earning;
  }

  private _updateEarningRate(earning: EmployeeEarning, rateInput: EmployeeEarningRate) {
    return this.store.selectOnce(PersonEditorState.canEarningHaveRates(earning.Id)).pipe(
      map(canHaveRates => {
        const rate = structuredClone(rateInput);
        if (canHaveRates) {
          rate.Amount = this.e.Amount.value || 0;
          rate.EffectiveDate = parseDatePickerValueToString(this.e.Date.value);
          rate.LastChangedOn = new Date();
          rate.LastEditorId = this.authService.getCurrentUserName();
        }
        return rate;
      })
    );
  }

  // #endregion
}
